import { isEditable } from '@testing-library/user-event/dist/utils';
import { MutationKeyConfig, QueryKeyConfig } from './api-helper';

export interface ProjectListRequestArgs {
  officeID?: number;
  customerID?: number;
}

export const projectsApiKeyStore = {
  getProjects: (): QueryKeyConfig => ({
    queryKey: [`GET_PROJECTS`],
    route: `/project`,
  }),
  getProjectStatus: (): QueryKeyConfig => ({
    queryKey: [`GET_PROJECT_STATUS`],
    route: `/project/projectstatus`,
  }),
  getProjectDetails: (projectID: number): QueryKeyConfig => ({
    queryKey: [`GET_PROJECT_DETAILS`, projectID],
    route: `/project/${projectID}`,
    isEnabled: !!projectID,
  }),
  getProjectDetailsRefeatch: (projectID: number, isEnabled: boolean): QueryKeyConfig => ({
    queryKey: [`GET_PROJECT_DETAILS`, projectID],
    route: `/project/${projectID}`,
    isEnabled: isEnabled,
  }),
  getProjectLotInfo: (projectID: number): QueryKeyConfig => ({
    queryKey: [`GET_PROJECT_DETAILS_LOTINFO`, projectID],
    route: `/project/projectlotinfo/${projectID}`,
  }),
  getCustomerProjects: (customerID: number): QueryKeyConfig => ({
    queryKey: [`GET_PROJECTS`, customerID],
    route: customerID ? `/project?customerID=${customerID}` : `/project`,
  }),
  getOfficeProjects: (officeID: any): QueryKeyConfig => ({
    queryKey: [`GET_OFFICE_PROJECTS`, officeID],
    route: `/project?officeID=${officeID}`,
    isEnabled: !!officeID && officeID !== undefined && officeID !== null,
  }),
  getOfficesProjects: (officeIDs: number[]): QueryKeyConfig => ({
    queryKey: [`GET_OFFICE_PROJECTS`, ...(officeIDs ? officeIDs : [])],
    route: `/project?${officeIDs?.map((o) => `offices=${o}`)?.join('&')}`,
    isEnabled: officeIDs !== undefined && officeIDs !== null,
  }),
  getProjectMunicipalities: (projectID: any): QueryKeyConfig => ({
    queryKey: [`GET_PROJECT_MUNICIPALITIES`, projectID],
    route: `/Project/${projectID}/MunicipalityMappings`,
  }),
  getProjectMarketAreas: (projectID: any): QueryKeyConfig => ({
    queryKey: [`GET_PROJECT_MARKETAREAS`, projectID],
    route: `/Project/${projectID}/MarketAreaMappings`,
  }),
  getProjectBuildings: (projectID: any): QueryKeyConfig => ({
    queryKey: [`GET_PROJECT_BUILDINGS`, projectID],
    route: `/Project/BuildingsForDD/${projectID}`,
  }),
  getProjectBuildingFloors: (buildingID: any): QueryKeyConfig => ({
    queryKey: [`GET_PROJECT_BUILDING_FLOORS`, buildingID],
    route: `/Project/BuildingFloorsForDD/${buildingID}`,
  }),
  checkDuplicateProjectCode: (
    projectID: number,
    officeID: number,
    projectCode: number,
    builderCode: string
  ): QueryKeyConfig => ({
    queryKey: [`CHECK_PROJECT_CODE_DUPLICATE`, projectID, officeID, projectCode, builderCode],
    route: `/Project/ProjectCodeUniqueCheck`,
    params: { projectID, officeID, projectCode, builderCode },
    isEnabled: !!projectID && !!officeID && !!projectCode,
  }),
  checkDuplicateLotAddress: (
    projectID: any,
    Address: string,
    unitID: number = 0,
    unitName: string = '',
    jobsite: string = '',
    JobNumber: string = ''
  ): QueryKeyConfig => ({
    queryKey: [`CHECK_LOT_DUPLICATE`, projectID, Address, unitID, unitName, jobsite, JobNumber],
    route: `/Project/CheckLotExists`,
    params: { projectID, Address, unitID, unitName, jobsite, JobNumber },
    isEnabled: !!projectID,
  }),
  createProject: (): MutationKeyConfig => ({
    mutationKey: [`CREATE_PROJECT`],
    route: `/project`,
    action: 'POST',
  }),
  updateProject: (): MutationKeyConfig => ({
    mutationKey: [`UPDATE_PROJECT`],
    route: `/project`,
    action: 'PUT',
  }),
  updateLotInfo: (): MutationKeyConfig => ({
    mutationKey: [`UPDATE_PROJECT_LOTINFO`],
    route: `/project/updatelotinfo`,
    action: 'POST',
  }),
  completeProject: (projectID: any): MutationKeyConfig => ({
    mutationKey: [`COMPLETE_PROJECT`],
    route: `/project/UpdateProjectCompleted?projectID=${projectID}`,
    action: 'PUT',
  }),
  getProjectProfileStatistics: (projectID?: number, formDate?: any, toDate?: any): QueryKeyConfig => ({
    queryKey: [`GET_PROJECT_PROFILESTAT`, projectID, formDate, toDate],
    route: `/Project/ProfileStatistics?ID=${projectID}&FromDate=${formDate}&ToDate=${toDate}`,
    isEnabled: !!projectID,
  }),

  createBuilding: (): MutationKeyConfig => ({
    mutationKey: [`CREATE_PROJECT_BUILDING`],
    route: `/project/addbuilding`,
    action: 'POST',
  }),
  createFloor: (): MutationKeyConfig => ({
    mutationKey: [`CREATE_PROJECT_BUILDING_FLOOR`],
    route: `/project/addfloor`,
    action: 'POST',
  }),
  createLot: (): MutationKeyConfig => ({
    mutationKey: [`CREATE_LOT`],
    route: `/project/addlot`,
    action: 'POST',
  }),
  createCommonArea: (): MutationKeyConfig => ({
    mutationKey: [`CREATE_PROJECT_BUILDING_COMMON_AREA`],
    route: `/project/addcommonarea`,
    action: 'POST',
  }),

  deleteBuilding: (buildingID: number): MutationKeyConfig => ({
    mutationKey: [`DELETE_BUILDING`, buildingID],
    route: `/project/deletebuilding/${buildingID}`,
    action: 'DELETE',
  }),
  deleteFloor: (floorID: number): MutationKeyConfig => ({
    mutationKey: [`DELETE_FLOOR`, floorID],
    route: `/project/deletefloor/${floorID}`,
    action: 'DELETE',
  }),
  deleteCommonArea: (otherID: number): MutationKeyConfig => ({
    mutationKey: [`DELETE_OTHER_ID`, otherID],
    route: `/project/deletecommonarea/${otherID}`,
    action: 'DELETE',
  }),
  deleteLot: (lotID: number): MutationKeyConfig => ({
    mutationKey: [`DELETE_OTHER_ID`, lotID],
    route: `/project/deletelot/${lotID}`,
    action: 'DELETE',
  }),
  getProjectDivisions: (projectID: any): QueryKeyConfig => ({
    queryKey: [`GET_PROJECT_DIVISIONS`, projectID],
    route: `/Project/${projectID}/DivisionMappings`,
  }),
  validateProject: (officeID: any, projectName: string, ProjectID: any): QueryKeyConfig => ({
    queryKey: [`VALIDATE_PROJECT`, officeID, projectName],
    route: `/Project/ProjectExists?OfficeID=${officeID}&ProjectName=${projectName}&ProjectID=${ProjectID}`,
  }),
  validateProjectCode: (
    projectCode: string,
    builderCode: string,
    officeID: number,
    ProjectID: number
  ): QueryKeyConfig => ({
    queryKey: [`VALIDATE_PROJECT_CODE`, projectCode, builderCode, officeID, ProjectID],
    route: `/Project/ProjectCodeUniqueCheck?ProjectCode=${projectCode}&BuilderCode=${builderCode}&OfficeID=${officeID}&ProjectID=${ProjectID}`,
  }),
  generateLotName: (projectID: any): QueryKeyConfig => ({
    queryKey: [`GENERATE_LOT_NAME`, projectID],
    route: `/Project/GenerateLotName/${projectID} `,
  }),

  getSearchProjects: (searchText: any): QueryKeyConfig => ({
    queryKey: [`GET_SEARCH_PROJECTS`, searchText],
    route: `/Project?SearchText=${searchText}`,
    isEnabled: !!searchText,
  }),

  getCustomerProjectsWithStatus: (customerID: number, status: string): QueryKeyConfig => ({
    queryKey: [`GET_CUSTOMER_PROJECTS_WITH_STATUS`, customerID, status],
    route: customerID ? `/project?customerID=${customerID}&Status=${status}` : `/project`,
  }),
  getOfficeProjectswithStatus: (officeID: any, status: string): QueryKeyConfig => ({
    queryKey: [`GET_OFFICE_PROJECTS_WITH_STATUS`, officeID, status],
    route: `/project?officeID=${officeID}&Status=${status}`,
    isEnabled: !!officeID && officeID !== undefined && officeID !== null,
  }),
  getProjectsWithStatus: (status: string): QueryKeyConfig => ({
    queryKey: [`GET_PROJECTS_WITH_STATUS`, status],
    route: status ? `/project?Status=${status}` : `/project`,
  }),
  checkDuplicateLotAddressUnitName: (
    projectID: any,
    Address: string,
    unitID: number = 0,
    unitName: string = '',
    jobsite: string = '',
    JobNumber: string = ''
  ): QueryKeyConfig => ({
    queryKey: [`CHECK_LOT_DUPLICATE`, projectID, Address, unitID, unitName, jobsite, JobNumber],
    route: `/Project/CheckLotExists`,
    params: { projectID, Address, unitID, unitName, jobsite, JobNumber },
    isEnabled: !!unitName && !!projectID,
  }),
  checkDuplicateLotAddressJobNumber: (
    projectID: any,
    Address: string,
    unitID: number = 0,
    unitName: string = '',
    jobsite: string = '',
    JobNumber: string = ''
  ): QueryKeyConfig => ({
    queryKey: [`CHECK_LOT_DUPLICATE`, projectID, Address, unitID, unitName, jobsite, JobNumber],
    route: `/Project/CheckLotExists`,
    params: { projectID, Address, unitID, unitName, jobsite, JobNumber },
    isEnabled: !!JobNumber && !!projectID,
  }),
  checkDuplicateLotAddressOfAddress: (
    projectID: any,
    Address: string,
    unitID: number = 0,
    unitName: string = '',
    jobsite: string = '',
    JobNumber: string = ''
  ): QueryKeyConfig => ({
    queryKey: [`CHECK_LOT_DUPLICATE`, projectID, Address, unitID, unitName, jobsite, JobNumber],
    route: `/Project/CheckLotExists`,
    params: { projectID, Address, unitID, unitName, jobsite, JobNumber },
    isEnabled: !!Address && !!projectID,
  }),
  checkDuplicateLotAddressJobsite: (
    projectID: any,
    Address: string,
    unitID: number = 0,
    unitName: string = '',
    jobsite: string = '',
    JobNumber: string = ''
  ): QueryKeyConfig => ({
    queryKey: [`CHECK_LOT_DUPLICATE`, projectID, Address, unitID, unitName, jobsite, JobNumber],
    route: `/Project/CheckLotExists`,
    params: { projectID, Address, unitID, unitName, jobsite, JobNumber },
    isEnabled: !!jobsite && !!projectID,
  }),

  generateLotNameByID: (projectID: any, isEnabled: boolean): QueryKeyConfig => ({
    queryKey: [`GENERATE_LOT_NAME`, projectID],
    route: `/Project/GenerateLotName/${projectID} `,
    isEnabled: isEnabled,
  }),

  uploadFileForBulkLotOrAddressCreation: (): MutationKeyConfig => ({
    mutationKey: [`UPLOAD_FILE`],
    route: `/Project/BulkLotUpload`,
    action: 'POST',
  }),
  uploadFileForBulkAddressCreation: (): MutationKeyConfig => ({
    mutationKey: [`UPLOAD_FILE`],
    route: `/Project/BulkAddressUpload`,
    action: 'POST',
  }),
  isActiveServiceProject: (): MutationKeyConfig => ({
    mutationKey: [`IS_ACTIVE_SERVICE_PROJECT`],
    route: `/Project/ProjectServiceStatusUpdate`,
    action: 'PUT',
  }),
};
